import { camelCase } from 'lodash'
import React, { ReactElement, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Radom, {
  IDiscountCode, IManagedPaymentMethodDetails, IManagedPaymentStatus, IManagedTokenDelegatePaymentMethodDetails,
  InputFieldWithValue, IPaymentLink, PaymentMethod, SellerProfile
} from '../api/Radom'
import Checkout, { zeroTravelRuleFieldsCollected } from '../components/Checkout'
import { PaymentSuccessMessage, PaymentUnderReviewMessage } from '../components/PaymentOutcome'
import { CheckoutParams } from '../components/PayWidget'
import Spinner from '../components/Spinner'
import { RADOM_COLORS } from '../util/Constants'
import { formatCurrency } from '../util/Currencies'
import LoadingCheckout from './LoadingCheckout'
import { trackCheckoutFunnelStep } from '../util/CheckoutFunnel'
import { clearPaymentSession, loadPaymentSession, savePaymentSession } from '../util/PaymentSession'
import PageLoadState from '../components/PageLoadState'
import { resolveNavigationUrl } from '../util/Util'

const PAYMENT_LINK_REDIRECT_DELAY_SECONDS = 3
const TOKEN_AMOUNT_KEY = 'Token amount'

// ---------------------------------------------------------------------------------------------------------------------
function PaymentLink(): ReactElement {
  const paymentLinkId = window.location.pathname.split('/').filter(Boolean).pop() ?? ''
  const [searchParams] = useSearchParams()

  const [total, setTotal] = useState(0)

  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [paymentLinkData, setPaymentLinkData] = useState<IPaymentLink>()
  const [sellerProfile, setSellerProfile] = useState<SellerProfile>()
  const [currency, setCurrency] = useState('USD')

  const [isPreProcessingPayment, setIsPreProcessingPayment] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [countdown, setCountdown] = useState(10)
  const [errorMessage, setErrorMessage] = useState('')
  const [inputFields, setInputFields] = useState<InputFieldWithValue[]>([])
  const [successElement, setSuccessElement] = useState<ReactElement>(<></>)

  const [discountResult, setDiscountResult] = useState({ success: false, element: <></> })
  const [discount, setDiscount] = useState<IDiscountCode>()
  const [discountLocked, setDiscountLocked] = useState(false)

  const [paymentStatus, setPaymentStatus] = useState<IManagedPaymentStatus>()
  const trackedFunnelStepsRef = useRef<Set<string>>(new Set())
  const managedPollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const [managedPaymentDetails, setManagedPaymentDetails] = useState<IManagedPaymentMethodDetails>()
  const [
    tokenDelegatePaymentDetails,
    setTokenDelegatePaymentDetails
  ] = useState<IManagedTokenDelegatePaymentMethodDetails>()

  const clearManagedPaymentPolling = (): void => {
    if (managedPollingIntervalRef.current) {
      clearInterval(managedPollingIntervalRef.current)
      managedPollingIntervalRef.current = null
    }
  }

  const trackFunnelStepOnce = (step: 'paymentConfirmed' | 'paymentFailed', sourceId: string): void => {
    if (trackedFunnelStepsRef.current.has(step)) {
      return
    }

    trackedFunnelStepsRef.current.add(step)
    void trackCheckoutFunnelStep({ sourceType: 'paymentLink', sourceId, rail: 'crypto', step })
  }

  const startManagedPaymentPolling = (managed: IManagedPaymentMethodDetails): void => {
    if (!paymentLinkData) {
      return
    }

    const sourceId = paymentLinkData.id
    clearManagedPaymentPolling()
    let appliedOutcome: string | undefined

    managedPollingIntervalRef.current = setInterval(() => {
      Radom.getManagedPaymentStatus(managed.paymentId).then(res => {
        setPaymentStatus(res)

        if (res.status === 'inReview') {
          if (appliedOutcome !== 'inReview') {
            appliedOutcome = 'inReview'
            setIsSuccess(true)
            setSuccessElement(<PaymentUnderReviewMessage />)
          }
          return
        }

        if (res.status === 'paymentConfirmed') {
          appliedOutcome = 'paymentConfirmed'
          trackFunnelStepOnce('paymentConfirmed', sourceId)
          clearPaymentSession(sourceId)
          setIsSuccess(true)
          clearManagedPaymentPolling()
          setSuccessElement(<PaymentSuccessMessage />)
        }

        if (res.status === 'failed' || res.status === 'expired') {
          if (res.status === 'failed') {
            trackFunnelStepOnce('paymentFailed', sourceId)
          }
          clearPaymentSession(sourceId)
          clearManagedPaymentPolling()
        }
      }).catch(() => {})
    }, 1000)
  }

  const startTokenDelegatePolling = (paymentId: string): void => {
    if (!paymentLinkData) {
      return
    }

    const sourceId = paymentLinkData.id
    clearManagedPaymentPolling()
    let appliedOutcome: string | undefined

    managedPollingIntervalRef.current = setInterval(() => {
      Radom.getManagedTokenPaymentStatus(paymentId).then(res => {
        setPaymentStatus(res)

        if (res.status === 'inReview') {
          if (appliedOutcome !== 'inReview') {
            appliedOutcome = 'inReview'
            setIsPreProcessingPayment(false)
            setIsSuccess(true)
            setSuccessElement(<PaymentUnderReviewMessage />)
          }
          return
        }

        if (res.status === 'paymentConfirmed') {
          appliedOutcome = 'paymentConfirmed'
          trackFunnelStepOnce('paymentConfirmed', sourceId)
          clearPaymentSession(sourceId)
          setIsPreProcessingPayment(false)
          setIsSuccess(true)
          clearManagedPaymentPolling()
          setSuccessElement(<PaymentSuccessMessage />)
        }

        if (res.status === 'failed') {
          trackFunnelStepOnce('paymentFailed', sourceId)
          clearPaymentSession(sourceId)
          clearManagedPaymentPolling()
          setIsPreProcessingPayment(false)
          setTokenDelegatePaymentDetails(undefined)
          setErrorMessage('Transaction failed, please try again.')
        }
      }).catch(() => {})
    }, 1000)
  }

  useEffect(() => {
    if (!paymentLinkData || managedPollingIntervalRef.current) {
      return
    }

    const session = loadPaymentSession(paymentLinkData.id)
    if (!session) {
      return
    }

    if (session.managed) {
      setDiscountLocked(true)
      setManagedPaymentDetails(session.managed)
      startManagedPaymentPolling(session.managed)
      return
    }

    if (session.tokenDelegate && session.delegateSubmitted) {
      setDiscountLocked(true)
      setIsPreProcessingPayment(true)
      setTokenDelegatePaymentDetails(session.tokenDelegate)
      startTokenDelegatePolling(session.tokenDelegate.paymentId)
    }
  }, [paymentLinkData])

  useEffect(() => clearManagedPaymentPolling, [])

  useEffect(() => {
    const discountCode = searchParams.get('discountCode')
    if (discountCode) {
      redeemDiscountCode(discountCode)
    }

    if (Array.isArray(paymentLinkData?.inputFields)) {
      const inputFields: InputFieldWithValue[] = []
      for (const input of paymentLinkData.inputFields) {
        const prefilledValue = searchParams.get(camelCase(input.inputLabel))
        inputFields.push({ ...input, value: prefilledValue ?? '' })
      }
      setInputFields(inputFields)
    }
  }, [searchParams, paymentLinkData])

  useEffect(() => {
    if (!paymentLinkData?.isPresale) {
      return
    }

    if (!window.location.pathname.startsWith('/payment_link/')) {
      return
    }

    const search = window.location.search
    window.location.replace(`/presale/${paymentLinkData.id}${search}`)
  }, [paymentLinkData])

  const redeemDiscountCode = async (discountCode: string): Promise<void> => {
    setDiscountResult({ success: false, element: <Spinner /> })

    Radom.redeemDiscountCode({ paymentLinkId, code: discountCode }).then((discount) => {
      setDiscount(discount)
      setDiscountResult({
        success: true,
        element: <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span>Discount applied!</span>
          <span style={{
            padding: 5,
            backgroundColor: RADOM_COLORS.SUCCESS,
            color: RADOM_COLORS.SUCCESS_LIGHT,
            borderRadius: 5,
            fontWeight: 600,
            fontSize: 12
          }}>
            {
              discount.model.modelType === 'percentage'
                ? new Intl.NumberFormat('en-US', {
                  style: 'percent',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                }).format(discount.model.amountOff / 100)
                : formatCurrency(currency, discount.model.amountOff)
            } OFF
          </span>
        </div>
      })
    }).catch(_ => {
      setDiscountResult({
        success: false,
        element: <span style={{ opacity: 0.5, fontSize: 12 }}>Invalid discount code</span>
      })
    })
  }

  const onCheckout = async ({
    managed,
    completeTokenDelegate,
    subscriptionOptions,
    travelRuleFields,
    freeInput
  }: CheckoutParams): Promise<void> => {
    setErrorMessage('')
    if (!paymentLinkData) {
      setErrorMessage('Payment link data not set. Please refresh and try again.')
      return
    }

    if (!travelRuleFields) {
      setErrorMessage('Travel rule fields are required. Please refresh and try again.')
      return
    }

    if (!trackedFunnelStepsRef.current.has('travelRuleCompleted')) {
      trackedFunnelStepsRef.current.add('travelRuleCompleted')
      void trackCheckoutFunnelStep({
        sourceType: 'paymentLink',
        sourceId: paymentLinkData.id,
        rail: 'crypto',
        step: 'travelRuleCompleted'
      })
    }

    if (managed) {
      const storedSession = loadPaymentSession(paymentLinkData.id)
      if (storedSession?.managed) {
        setDiscountLocked(true)
        setManagedPaymentDetails(storedSession.managed)
        startManagedPaymentPolling(storedSession.managed)
        return
      }

      const { selectedMethod: method } = managed
      const safeProducts = Array.isArray(paymentLinkData.products) ? paymentLinkData.products : []
      const checkoutAmount = freeInput?.amount ?? total
      const checkoutCurrency = freeInput?.currency ?? currency
      const presaleProduct = safeProducts.find(product => product.productType?.Presale?.Token)
      const tokenAmountDecimals = Number(presaleProduct?.productType?.Presale?.Token?.decimals ?? 6)
      const canDeriveFreeInputPresaleAmount =
        !!presaleProduct &&
        paymentLinkData.customizations.allowFreeInputAmount &&
        checkoutCurrency === presaleProduct.currency
      const derivedPurchaseAmount = canDeriveFreeInputPresaleAmount
        ? checkoutAmount
        : safeProducts.reduce((sum, product) => sum + Number(product.price ?? 0), 0)
      const derivedTokenAmount =
        presaleProduct &&
        Number(presaleProduct.price) > 0 &&
        Number.isFinite(derivedPurchaseAmount)
          ? (derivedPurchaseAmount / Number(presaleProduct.price))
            .toFixed(Math.max(0, Math.min(12, tokenAmountDecimals)))
            .replace(/(\.\d*?[1-9])0+$/, '$1')
            .replace(/\.0+$/, '')
          : undefined

      const orderData = inputFields
        .map(c => {
          return {
            key: c.inputLabel,
            value: c.value.toString()
          }
        })
        .filter(entry => entry.key !== TOKEN_AMOUNT_KEY)

      if (derivedTokenAmount && derivedTokenAmount.length > 0) {
        orderData.push({
          key: TOKEN_AMOUNT_KEY,
          value: derivedTokenAmount
        })
      }

      let paymentMethod: PaymentMethod = {
        managed: {
          method: {
            network: method.tokenInfo?.networkName ?? method.name,
            token: method.tokenInfo?.tokenAddress ?? undefined
          }
        }
      }

      if (subscriptionOptions) {
        if (subscriptionOptions.autopay) {
          if (!method.tokenInfo) {
            setErrorMessage('Cannot set up an autopay subscription without an EVM based token')
            return
          }

          paymentMethod = {
            managedTokenDelegate: {
              buyerAddress: subscriptionOptions.autopay.address,
              network: method.tokenInfo.networkName,
              token: method.tokenInfo.tokenAddress
            }
          }
        }

        if (subscriptionOptions.emailInvoice) {
          if (!subscriptionOptions.emailInvoice.emailAddress) {
            setErrorMessage('Cannot set up an email invoice subscription without an email address')
            return
          }
        }
      }

      setIsPreProcessingPayment(true)

      try {
        const res = await Radom.createPaymentLinkOrder({
          paymentLinkId: paymentLinkData.id,
          paymentMethod,
          orderData,
          amount: paymentLinkData.customizations.allowFreeInputAmount ? checkoutAmount : undefined,
          currency: paymentLinkData.customizations.allowFreeInputAmount ? checkoutCurrency : undefined,
          orderProductData: [],
          discountCode: discount?.code,
          emailAddress: subscriptionOptions?.emailInvoice?.emailAddress.toLowerCase(),
          travelRuleFields
        })

        if (!trackedFunnelStepsRef.current.has('paymentInitiated')) {
          trackedFunnelStepsRef.current.add('paymentInitiated')
          void trackCheckoutFunnelStep({
            sourceType: 'paymentLink',
            sourceId: paymentLinkData.id,
            rail: 'crypto',
            step: 'paymentInitiated',
            metadata: {
              network: method.tokenInfo?.networkName ?? method.name,
              tokenAddress: method.tokenInfo?.tokenAddress
            }
          })
        }

        setDiscountLocked(true)

        if (res.paymentMethodDetails.managed) {
          const managedDetails = res.paymentMethodDetails.managed
          setManagedPaymentDetails(managedDetails)
          savePaymentSession(paymentLinkData.id, { managed: managedDetails })
          startManagedPaymentPolling(managedDetails)
        } else if (res.paymentMethodDetails.managedTokenDelegate) {
          const delegateDetails = res.paymentMethodDetails.managedTokenDelegate
          setTokenDelegatePaymentDetails(delegateDetails)
          savePaymentSession(paymentLinkData.id, { tokenDelegate: delegateDetails })
        } else {
          throw new Error('Unexpected empty managed payment method details')
        }
      } catch (err) {
        console.error(err)
        setErrorMessage(err.reason || err.message)
        throw err
      }

      setIsPreProcessingPayment(false)
      return
    }

    if (completeTokenDelegate) {
      const { paymentId, signature } = completeTokenDelegate

      setIsPreProcessingPayment(true)
      try {
        await Radom.completeTokenDelegatePayment(paymentId, signature)
        if (tokenDelegatePaymentDetails) {
          savePaymentSession(paymentLinkData.id, {
            tokenDelegate: tokenDelegatePaymentDetails,
            delegateSubmitted: true
          })
        }
        startTokenDelegatePolling(paymentId)
      } catch (err) {
        setIsPreProcessingPayment(false)
        setErrorMessage(err.reason || err.message)
        throw err
      }
      return
    }

    setErrorMessage('Oops, something went wrong.')
  }

  useEffect(() => {
    if (!paymentLinkData) {
      return
    }

    if (trackedFunnelStepsRef.current.has('viewed')) {
      return
    }

    trackedFunnelStepsRef.current.add('viewed')
    void trackCheckoutFunnelStep({
      sourceType: 'paymentLink',
      sourceId: paymentLinkData.id,
      rail: 'crypto',
      step: 'viewed'
    })
  }, [paymentLinkData?.id])

  useEffect(() => {
    let cancelled = false

    const loadPaymentLink = async (): Promise<void> => {
      try {
        const data = await Radom.getPaymentLink(paymentLinkId)
        if (cancelled) return

        const safeInputFields = Array.isArray(data.inputFields) ? data.inputFields : []
        setPaymentLinkData(data)
        setInputFields(safeInputFields.map(c => {
          return { ...c, value: '' }
        }))

        if (data.sellerAddress) {
          try {
            const profile = await Radom.getSellerProfile(data.sellerAddress)
            if (cancelled) return
            setSellerProfile(profile)
          } catch (profileErr) {
            console.warn('Failed to load seller profile for payment link', profileErr)
          }
        }
      } catch (err) {
        console.error(err)
        if (!cancelled) {
          setLoadError(err?.message ?? err?.response?.data?.error ?? 'Payment link is unavailable')
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    void loadPaymentLink()

    return () => {
      cancelled = true
    }
  }, [paymentLinkId])

  useEffect(() => {
    let countdownTimer
    let redirectTimer

    if (isSuccess && paymentLinkData?.successUrl) {
      // Start the countdown timer
      const initialCountdown = PAYMENT_LINK_REDIRECT_DELAY_SECONDS
      countdownTimer = setInterval(() => {
        setCountdown(countdown => Math.max(countdown - 1, 0))
      }, 1000)

      setCountdown(initialCountdown)
      // Start the redirect timer
      redirectTimer = setTimeout(() => {
        window.location.href = resolveNavigationUrl(paymentLinkData?.successUrl)
      }, PAYMENT_LINK_REDIRECT_DELAY_SECONDS * 1000)
    }

    // Clean up the timers when the component unmounts or the values change
    return () => {
      clearInterval(countdownTimer)
      clearTimeout(redirectTimer)
    }
  }, [isSuccess, paymentLinkData])

  // Update the countdown label whenever the countdown value changes
  useEffect(() => {
    if (countdown > 0 && countdown < PAYMENT_LINK_REDIRECT_DELAY_SECONDS && isSuccess) {
      document.title = `Redirecting in ${countdown} seconds`
    } else if (isSuccess && countdown === 0) {
      document.title = 'Redirecting...'
    }
  }, [countdown, isSuccess])

  if (isLoading) {
    return <LoadingCheckout />
  }

  if (loadError || !paymentLinkData) {
    return (
      <PageLoadState
        title="Payment link unavailable"
        message={loadError || 'This payment link could not be loaded right now.'}
        onRetry={async () => {
          window.location.reload()
        }}
      />
    )
  }

  const safeProducts = Array.isArray(paymentLinkData.products) ? paymentLinkData.products : []

  if (safeProducts.length <= 0 && !paymentLinkData.customizations.allowFreeInputAmount) {
    return <div style={{
      minWidth: '100vw',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'white',
      color: '#111827',
      padding: 24,
      textAlign: 'center'
    }}>
      This payment link is currently unavailable.
    </div>
  }

  const normalizedPaymentLinkTotal = paymentLinkData.total == null
    ? undefined
    : Number(paymentLinkData.total)
  const checkoutCurrency =
    paymentLinkData.currency ??
    safeProducts[0]?.currency ??
    currency

  // For a pyament link, all travel rule fields must be collected from the end user at checkout time
  // Hence travelRuleFieldsCollected argument must be set to collect all fields
  return <Checkout
    checkoutData={{
      ...paymentLinkData,
      total:
        normalizedPaymentLinkTotal !== undefined && Number.isFinite(normalizedPaymentLinkTotal)
          ? normalizedPaymentLinkTotal
          : undefined,
      currency: checkoutCurrency,
      sellerName: sellerProfile?.name ?? paymentLinkData.sellerName,
      sellerLogoUrl: sellerProfile?.logoUrl ?? paymentLinkData.sellerLogoUrl,
      products: safeProducts.map(p => {
        return { product: p, quantity: 1 }
      }),
      inputFields
    }}
    onInputFieldsChange={setInputFields}
    onCheckout={onCheckout}
    managedPaymentDetails={managedPaymentDetails}
    tokenDelegatePaymentDetails={tokenDelegatePaymentDetails}
    onChangePaymentMethod={() => {
      clearManagedPaymentPolling()
      if (paymentLinkData) {
        clearPaymentSession(paymentLinkData.id)
      }
      setManagedPaymentDetails(undefined)
      setTokenDelegatePaymentDetails(undefined)
      setPaymentStatus(undefined)
      setErrorMessage('')
      setIsPreProcessingPayment(false)
    }}
    isPreProcessingPayment={isPreProcessingPayment}
    isSuccess={isSuccess}
    errorMessage={errorMessage}
    onCurrency={currency => setCurrency(currency)}
    onTotalChange={total => setTotal(total)}
    onDiscountCodeEntered={async discountCode => await redeemDiscountCode(discountCode)}
    discountResult={discountResult}
    discount={discount}
    discountLocked={discountLocked}
    resetDiscountCode={() => {
      setDiscountResult({ success: false, element: <></> })
      setDiscount(undefined)
    }}
    successElement={successElement}
    paymentStatus={paymentStatus}
    travelRuleFieldsCollected={zeroTravelRuleFieldsCollected()}
  />
}

export default PaymentLink
